/**
 * Data layer for `cost-etl-import-ad-network-spend-compute-net-margi`. Thin typed fetch wrappers over /api/v1/cost-etl-import-ad-network-spend-compute-net-margi.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface SpendRow {
	id: number;
	tenant_id: string;
	network: string;
	external_id: string;
	campaign_id: string;
	date: string;
	spend_cents: number;
	impressions: number;
	clicks: number;
	created_at: number;
}

export interface MarginRow {
	campaign_id: string;
	date: string;
	spend_cents: number;
	revenue_cents: number;
	margin_cents: number;
	clicks: number;
	impressions: number;
	conversions: number;
}

export interface ListSpendResponse {
	items: SpendRow[];
}

export interface MarginResponse {
	items: MarginRow[];
}

export interface MarginFilters {
	campaign_id?: string;
	from?: string;
	to?: string;
	[key: string]: string | undefined;
}

const BASE = "/api/v1/cost-etl-import-ad-network-spend-compute-net-margi";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function qs(params: Record<string, string | undefined>): string {
	const parts: string[] = [];
	for (const k of Object.keys(params)) {
		const v = params[k];
		if (v !== undefined && v !== "") {
			parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
		}
	}
	return parts.length ? `?${parts.join("&")}` : "";
}

export async function listSpend(): Promise<ListSpendResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListSpendResponse>(res);
}

export async function getMargin(filters: MarginFilters = {}): Promise<MarginResponse> {
	const res = await fetch(`${BASE}/margin${qs(filters)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<MarginResponse>(res);
}

export const costEtlImportAdNetworkSpendComputeNetMargiQueryKeys = {
	all: ["cost-etl-import-ad-network-spend-compute-net-margi"] as const,
	spend: () => [...costEtlImportAdNetworkSpendComputeNetMargiQueryKeys.all, "spend"] as const,
	margin: (filters: MarginFilters) =>
		[...costEtlImportAdNetworkSpendComputeNetMargiQueryKeys.all, "margin", filters] as const,
};
